import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { CustomerStatusBadge } from '@plato/app-shared/components/customer-status-badge';
import { PageTitle } from '@plato/app-shared/components/page-title';
import { type CustomerBusinessStatus, SrCustomerStatus, SrInsightType } from '@plato/data/starrocks/types';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { TitleWithCopyButton } from '@plato/ui-legacy/components/ui/buttons/copy-button';
import { PageHeader } from '@plato/ui-legacy/components/ui/page-header';
import { useReferrer } from '@plato/ui-legacy/lib/hooks/use-referrer';
import { useNavigate } from 'react-router-dom';
import type { CustomerBasics } from '../../../server/types/infer-trpc-types.ts';
import { api } from '../../../utils/api.ts';
import { CustomerDetailNewActivity } from './customer-detail-new-activity.tsx';
import { UpdateCustomerBusinessStatusDialog } from './customer-detail-update-business-status-dialog.tsx';

type Props = {
    customer: CustomerBasics | undefined;
};

export function CustomerDetailHeader({ customer }: Props) {
    const { isInternal: lastPathIsPlato } = useReferrer();
    const { enablePlatoStatus } = useAppConfig();
    const navigate = useNavigate();

    const erpCustomerId = customer?.customerId ?? '';
    const isInactive = customer?.platoStatus === SrCustomerStatus.INACTIVE;
    const hasStatus = enablePlatoStatus && !!customer?.platoStatus;

    const { data: transactionSummary } = api.platoUser.getDetailCustomer.getOpenTransactionSummary.useQuery(
        { erpCustomerId },
        { enabled: hasStatus && !!erpCustomerId && !isInactive },
    );

    const { data: customerFinancials } = api.platoUser.getDetailCustomer.getFinancials.useQuery(
        { erpCustomerId },
        { enabled: hasStatus && !!erpCustomerId && !isInactive },
    );

    const { data: churnInsights } = api.platoUser.query.insights.getManyInsights.getListWithFilters.useQuery(
        {
            customerId: erpCustomerId,
            insightTypes: [SrInsightType.CHURN_WARNING, SrInsightType.GLOBAL_CHURN_WARNING],
        },
        { enabled: hasStatus && !!erpCustomerId && !isInactive },
    );

    const goBack = (): void => {
        if (lastPathIsPlato) {
            void navigate(-1);
        } else {
            void navigate('/customers');
        }
    };

    const customerId = customer?.customerId ?? '';
    const customerName = customer?.customerName ?? '';
    const titleText = [customerId, customerName].filter(Boolean).join(' ');

    return (
        <>
            {customerName && <PageTitle title={customerName} />}
            <PageHeader
                onBack={goBack}
                title={
                    customerId ? (
                        <TitleWithCopyButton text={titleText} textToCopy={customerId} />
                    ) : (
                        <span>{customerName}</span>
                    )
                }
                actions={<CustomerDetailNewActivity customer={customer} />}
            >
                {hasStatus && (
                    <CustomerStatusBadge
                        status={customer.platoStatus}
                        transactionSummary={transactionSummary}
                        customerFinancials={customerFinancials}
                        hasChurnWarnings={churnInsights ? churnInsights.length > 0 : undefined}
                        onStatusChangeRequest={(mode: CustomerBusinessStatus) => {
                            void DialogStateManager.show(UpdateCustomerBusinessStatusDialog, {
                                customerId: customer.customerId,
                                mode,
                            });
                        }}
                    />
                )}
            </PageHeader>
        </>
    );
}
